/**
 * Shared helpers for the search popup panels (idle / results / empty).
 * Mounts content from the layers-group "Search popup" section into the modal.
 */
(function () {
  const MOUNTED_ATTR = 'data-search-panels-mounted';
  const PRODUCT_CARD_SELECTOR =
    '.js-product-card-wrapper, .product-card, .product-card-wrapper, .card-wrapper';

  function getConfig() {
    return document.querySelector('[data-search-popup-config]');
  }

  function mountPanels(root) {
    if (!root || root.hasAttribute(MOUNTED_ATTR)) return;

    const config = getConfig();
    if (!config) {
      root.setAttribute(MOUNTED_ATTR, 'true');
      bindPhraseChips(root);
      bindIdleMobileTabs(root);
      return;
    }

    const idleSource = config.querySelector('[data-search-idle-source]');
    const idlePanel = root.querySelector('[data-search-panel="idle"]');

    if (idlePanel && idleSource) {
      idlePanel.innerHTML = idleSource.innerHTML;
    }

    root.setAttribute(MOUNTED_ATTR, 'true');
    bindPhraseChips(root);
    bindIdleMobileTabs(root);
    observeFavoritesForTopProduct(root);
  }

  function activateMobileTab(trigger) {
    if (!trigger) return;
    const tablist = trigger.closest('.search-popup__mobile-tabs');
    const layout =
      trigger.closest('.search-popup__layout') ||
      trigger.closest('[data-search-panel]') ||
      trigger.parentElement;
    if (!tablist || !layout) return;

    const tabTriggers = Array.from(tablist.querySelectorAll('.search-popup__tab.tab')).filter(
      (tab) => !tab.hidden
    );
    const activePanelId = trigger.getAttribute('data-mobile-tab');

    tabTriggers.forEach((tab) => {
      const isActive = tab === trigger;
      tab.classList.toggle('active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    const panelIds = new Set(
      tabTriggers.map((tab) => tab.getAttribute('data-mobile-tab')).filter(Boolean)
    );
    panelIds.forEach((panelId) => {
      const panel = layout.querySelector(`#${CSS.escape(panelId)}`);
      panel?.classList.toggle('mobile-tab__content--active', panelId === activePanelId);
    });
  }

  function bindMobileTabClicks(host) {
    if (!host || host.hasAttribute('data-mobile-tabs-bound')) return;
    host.setAttribute('data-mobile-tabs-bound', 'true');
    host.addEventListener('click', (event) => {
      if (!window.matchMedia('(max-width: 749px)').matches) return;
      const tab = event.target.closest('.search-popup__tab.tab');
      if (!tab || tab.hidden) return;
      activateMobileTab(tab);
    });
  }

  function syncDefaultMobileTab(host) {
    if (!host || !window.matchMedia('(max-width: 749px)').matches) return;
    const state = host.getAttribute('data-search-state') || 'idle';
    const visibleState = state === 'empty' ? 'idle' : state;
    const visiblePanels = [...host.querySelectorAll('[data-search-panel]')].filter((el) => !el.hidden);
    const panel =
      visiblePanels.find((el) => el.getAttribute('data-search-panel') === visibleState) ||
      visiblePanels[0];
    if (!panel) return;
    const tabs = Array.from(
      (panel.querySelector('.search-popup__mobile-tabs') || panel).querySelectorAll('.search-popup__tab.tab')
    ).filter((tab) => !tab.hidden);
    const defaultTab =
      tabs.find((tab) => tab.classList.contains('active')) ||
      tabs.find((tab) => tab.getAttribute('data-mobile-tab')?.includes('products')) ||
      tabs[0];
    activateMobileTab(defaultTab);
  }

  function dedupeMobileProductTabs(root) {
    if (!root) return;
    const layout = root.querySelector('.search-popup__layout') || root;
    const tablists = Array.from(layout.querySelectorAll('.search-popup__mobile-tabs'));
    tablists.slice(1).forEach((el) => el.remove());

    const productTabs = Array.from(
      layout.querySelectorAll('[data-tab-products], [data-mobile-tab="predictive-search-results-products"]')
    );
    productTabs.slice(1).forEach((el) => el.remove());

    layout.querySelector('#predictive-search-products')?.setAttribute('hidden', '');
  }

  function bindIdleMobileTabs(root) {
    const host = root.closest('predictive-search, constructor-autocomplete');
    if (!host) return;
    bindMobileTabClicks(host);
    host._mobileTabsInitialised = false;
    syncDefaultMobileTab(host);
    if (typeof host.mobileTabs === 'function') host.mobileTabs();
    if (typeof host._mobileTabs === 'function') host._mobileTabs();
  }

  function bindPhraseChips(root) {
    root
      .querySelectorAll('[data-search-phrase], .search-popup__section--phrases .search-popup__chip-link')
      .forEach((el) => {
        if (el.hasAttribute('data-phrase-bound')) return;
        el.setAttribute('data-phrase-bound', 'true');

        // Keep focus on the search input so results can replace the idle panel
        // without a focusout → modal.close race (chip is removed from the DOM).
        el.addEventListener('mousedown', (event) => {
          event.preventDefault();
        });

        el.addEventListener('click', (event) => {
          const value = el.getAttribute('data-search-phrase-value') || el.textContent.trim();
          const host = root.closest('predictive-search, constructor-autocomplete');
          const input = host?.querySelector('input[type="search"]');
          if (!input || !value) return;

          event.preventDefault();
          const modal = host.closest('search-modal');
          modal?.open?.();
          input.focus({ preventScroll: true });
          input.value = value;
          // SearchForm debounces `input` → onChange; avoid a second immediate call.
          input.dispatchEvent(new Event('input', { bubbles: true }));
        });
      });
  }

  function getFirstFavoriteCard(favoritesList) {
    if (!favoritesList) return null;

    for (const item of favoritesList.children) {
      if (item.getAttribute('aria-hidden') === 'true') continue;
      const card = item.querySelector(PRODUCT_CARD_SELECTOR);
      if (card) return card;
    }
    return null;
  }

  function syncTopProductFromFavorites(root) {
    const topCard = root.querySelector('[data-search-top-product]');
    const favoritesList = root.querySelector('.search-popup__products--favorites');
    if (!topCard || !favoritesList) return;

    const firstCard = getFirstFavoriteCard(favoritesList);
    if (!firstCard) return;

    const handle =
      firstCard.closest('[data-product-handle]')?.getAttribute('data-product-handle') ||
      firstCard.getAttribute('data-product-handle') ||
      firstCard.querySelector('a[href*="/products/"]')?.getAttribute('href') ||
      '';
    if (topCard.dataset.syncedFrom === handle && topCard.querySelector(PRODUCT_CARD_SELECTOR)) {
      return;
    }

    topCard.replaceChildren(firstCard.cloneNode(true));
    topCard.dataset.syncedFrom = handle;
    window.updateWishlistStateImmediately?.(topCard);
  }

  function observeFavoritesForTopProduct(root) {
    const favoritesList = root.querySelector('.search-popup__products--favorites');
    if (!favoritesList || favoritesList.hasAttribute('data-top-sync-observed')) return;

    favoritesList.setAttribute('data-top-sync-observed', 'true');
    syncTopProductFromFavorites(root);

    const observer = new MutationObserver(() => {
      syncTopProductFromFavorites(root);
    });
    observer.observe(favoritesList, { childList: true, subtree: true });
  }

  function updateNoMatchNote(host, visible) {
    const note = host.querySelector('[data-search-no-match]');
    if (!note) return;

    if (!visible) {
      note.hidden = true;
      return;
    }

    const template = note.getAttribute('data-template') || '';
    const query = host.querySelector('input[type="search"]')?.value?.trim() || '';
    note.textContent = template.split('__terms__').join(query);
    note.hidden = false;
  }

  function bindExpandedInput(host) {
    const input = host.querySelector('input[type="search"]');
    if (!input) return;
    input.setAttribute('aria-expanded', 'true');
    const panels = host.querySelector('[data-search-panels]');
    if (!panels) return;
    if (!panels.id) panels.id = 'search-popup-panels';
    input.setAttribute('aria-controls', panels.id);
    input.setAttribute('aria-owns', panels.id);
  }

  function revealIdleRecommendations(host) {
    bindMobileTabClicks(host);
    syncDefaultMobileTab(host);

    requestAnimationFrame(() => {
      host.querySelectorAll('exponea-reco').forEach((el) => {
        if (typeof el.ensureLoaded === 'function') el.ensureLoaded();
      });
      observeFavoritesForTopProduct(host);
      syncTopProductFromFavorites(host);
    });
  }

  function setSearchState(host, state) {
    if (!host) return;
    host.setAttribute('data-search-state', state);

    const panels = host.querySelector('[data-search-panels]');
    if (!panels) return;

    mountPanels(panels);

    const visiblePanel = state === 'empty' ? 'idle' : state;
    panels.querySelectorAll('[data-search-panel]').forEach((panel) => {
      const isActive = panel.getAttribute('data-search-panel') === visiblePanel;
      panel.hidden = !isActive;
      if (panel.hasAttribute('data-predictive-search')) {
        panel.style.display = isActive ? 'block' : 'none';
      }
    });

    updateNoMatchNote(host, state === 'empty');
  }

  function showLoadingSkeleton(host) {
    if (!host) return;
    const resultsPanel = host.querySelector('[data-predictive-search]');
    const template = document.getElementById('search-popup-results-skeleton');
    if (!resultsPanel || !template) return;

    resultsPanel.replaceChildren(template.content.cloneNode(true));
    host.setAttribute('loading', '');
    setSearchState(host, 'results');
  }

  function showIdle(host) {
    setSearchState(host, 'idle');
    host.removeAttribute('results');
    host.removeAttribute('loading');
    host.removeAttribute('open');
    bindExpandedInput(host);
    revealIdleRecommendations(host);
  }

  function showResults(host) {
    setSearchState(host, 'results');
    host.setAttribute('open', '');
    host.setAttribute('results', '');
    const input = host.querySelector('input[type="search"]');
    if (input) input.setAttribute('aria-expanded', 'true');
    bindMobileTabClicks(host);
    dedupeMobileProductTabs(host.querySelector('[data-predictive-search]'));
    syncDefaultMobileTab(host);
  }

  function showEmpty(host) {
    setSearchState(host, 'empty');
    host.setAttribute('open', '');
    host.setAttribute('results', '');
    host.removeAttribute('loading');
    bindExpandedInput(host);
    revealIdleRecommendations(host);
  }

  function remountAllPanels() {
    document.querySelectorAll('[data-search-panels]').forEach((panels) => {
      panels.removeAttribute(MOUNTED_ATTR);
      mountPanels(panels);
    });
  }

  function openEditorPreview() {
    const modal = document.querySelector('search-modal');
    if (!modal) return;

    if (typeof modal.open === 'function') {
      modal.open();
    }

    remountAllPanels();

    const host = modal.querySelector('predictive-search, constructor-autocomplete');
    if (host) showIdle(host);
  }

  function closeEditorPreview() {
    const modal = document.querySelector('search-modal');
    if (modal?.isOpen?.() && typeof modal.close === 'function') {
      modal.close(false);
    }
  }

  function isSearchPopupSection(el) {
    return Boolean(el?.classList?.contains('shopify-section-search-popup-config'));
  }

  function initDesignModePreview() {
    if (!window.Shopify?.designMode) return;

    document.addEventListener('shopify:section:select', (event) => {
      if (!isSearchPopupSection(event.target)) return;
      openEditorPreview();
    });

    document.addEventListener('shopify:section:deselect', (event) => {
      if (!isSearchPopupSection(event.target)) return;
      closeEditorPreview();
    });

    document.addEventListener('shopify:section:load', (event) => {
      if (!isSearchPopupSection(event.target)) return;
      remountAllPanels();
      openEditorPreview();
    });

    document.addEventListener('shopify:block:select', (event) => {
      if (!event.target.closest('.shopify-section-search-popup-config')) return;
      openEditorPreview();
    });
  }

  window.SearchPopup = {
    mountPanels,
    remountAllPanels,
    setSearchState,
    showLoadingSkeleton,
    showIdle,
    showResults,
    showEmpty,
    openEditorPreview,
    bindPhraseChips,
    activateMobileTab,
    bindMobileTabClicks,
    syncDefaultMobileTab,
    dedupeMobileProductTabs,
  };

  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  onReady(() => {
    document.querySelectorAll('[data-search-panels]').forEach(mountPanels);
    initDesignModePreview();
  });
})();
